import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chart as ChartJS } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import annotationPlugin from 'chartjs-plugin-annotation'; // 引入 annotation 插件
import './ALC.css';

ChartJS.register(annotationPlugin);

const ALC = ({ data }) => {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        return window.innerWidth <= 768;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const chartGroups = useMemo(() => {
        const chunks = [];
        const chunkSize = isMobile ? 6 : 14;

        for (let i = 0; i < data.length; i += chunkSize) {
            const chunk = data.slice(i, i + chunkSize);
            if (chunk.length < chunkSize) {
                const paddingCount = chunkSize - chunk.length;
                const padding = Array.from({ length: paddingCount }, () => ({
                    id: 0,
                    name: '',
                    value: 0,
                    warning_low: 0,
                    warning_high: 0,
                    action_low: 0,
                    action_high: 0,
                }));
                chunks.push([...chunk, ...padding]);
            } else {
                chunks.push(chunk);
            }
        }
        return chunks;
    }, [data, isMobile]);

    return (
        <div className="alc-wrapper">
            {chartGroups.map((chunk, index) => {
                const labels = chunk.map((item) => item.name || '');
                const barData = chunk.map((item) =>
                    item.value === null || item.value === undefined ? null : item.value
                );

                const datasets = [
                    {
                        type: 'bar',
                        label: '地錨荷重計',
                        data: barData,
                        backgroundColor: 'rgba(54, 162, 235, 0.7)',
                    },
                ];

                const annotations = chunk.reduce((acc, item, idx) => {
                    if (!item.id) return acc;
                    acc[`warningLow-${idx}`] = {
                        type: 'line',
                        xMin: idx - 0.4,
                        xMax: idx + 0.4,
                        yMin: item.warning_low,
                        yMax: item.warning_low,
                        borderColor: '#f5a623',
                        borderWidth: 2,
                    };
                    acc[`warningHigh-${idx}`] = {
                        type: 'line',
                        xMin: idx - 0.4,
                        xMax: idx + 0.4,
                        yMin: item.warning_high,
                        yMax: item.warning_high,
                        borderColor: '#f5a623',
                        borderWidth: 2,
                    };
                    acc[`actionLow-${idx}`] = {
                        type: 'line',
                        xMin: idx - 0.4,
                        xMax: idx + 0.4,
                        yMin: item.action_low,
                        yMax: item.action_low,
                        borderColor: '#d0021b',
                        borderWidth: 2,
                    };
                    acc[`actionHigh-${idx}`] = {
                        type: 'line',
                        xMin: idx - 0.4,
                        xMax: idx + 0.4,
                        yMin: item.action_high,
                        yMax: item.action_high,
                        borderColor: '#d0021b',
                        borderWidth: 2,
                    };
                    return acc;
                }, {});

                const chartData = { labels, datasets };

                const options = {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        customCrosshair: {
                            enabled: true,
                            unit: 'Ton',
                        },
                        legend: {
                            display: index === 0,
                            labels: {
                                generateLabels: (chart) => {
                                    const originalLabels =
                                        ChartJS.defaults.plugins.legend.labels.generateLabels(
                                            chart
                                        );
                                    originalLabels.push(
                                        {
                                            text: '警戒值',
                                            fillStyle: '#f5a623',
                                            strokeStyle: '#f5a623',
                                            lineWidth: 2,
                                        },
                                        {
                                            text: '行動值',
                                            fillStyle: '#d0021b',
                                            strokeStyle: '#d0021b',
                                            lineWidth: 2,
                                        }
                                    );
                                    return originalLabels;
                                },
                            },
                        },
                        annotation: { annotations },
                        tooltip: {
                            callbacks: {
                                title: () => '', // 不顯示儀器 ID
                                label: (context) => {
                                    const rawValue = chunk[context.dataIndex]?.value;
                                    if (rawValue === null || rawValue === undefined) {
                                        return '無資料';
                                    }
                                    const value = context.parsed.y;
                                    return `${value} Ton`;
                                },
                            },
                        },
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    chunkHighlight: {
                        enabled: true, // 這一行控制是否啟用
                        highlightColor: 'rgba(0, 0, 0, 0.08)', // 這可以自行調整高亮顏色
                    },
                    hover: { mode: 'index', intersect: false },
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: false,
                                maxRotation: 45,
                                minRotation: 45,
                            },
                        },
                        y: {
                            title: { display: true, text: '地錨錨度 (Ton)' },
                            suggestedMin: 0,
                            suggestedMax: 120,
                        },
                    },
                    onClick: (event, elements, chart) => {
                        if (elements.length > 0) {
                            const element = elements[0];
                            const barIndex = element.index;
                            const instrumentId = chunk[barIndex].id;
                            if (instrumentId) {
                                navigate(`device/${chunk[barIndex].name}`);
                            }
                        }
                    },
                };

                return (
                    <div
                        key={`chart-group-${isMobile ? 'mobile' : 'desktop'}-${index}`}
                        className="ALC-container"
                    >
                        <Chart redraw type="bar" data={chartData} options={options} />
                    </div>
                );
            })}
        </div>
    );
};

export default ALC;
